import type { AspectRatio } from '@lumen5/luminary';
import { createAction } from '@reduxjs/toolkit';

import type {
    CreationSourceEnum,
    LanguageEnum,
    VideoApprovalStatusEnum,
    VideoVisibility,
} from 'js/src/client';
import type {
    AsyncGenerationData,
    EDITOR_TAB_OPTIONS,
    PendingChart,
    RecommendedBlueprint,
    SCENE_GROUP_LOADING_TYPES,
    SceneGroupLoadingContextType,
    STAGES,
    VIDEO_INIT_TYPE,
} from 'js/src/data/app/app-types';
import { APP_ACTIONS } from 'js/src/data/app/app-types';
import type { NEW_VIDEO_FLOW_CREATION_METHOD } from 'js/src/libs/consts';

export const setEditorTabsVisibility = createAction<boolean>(
    APP_ACTIONS.SET_EDITOR_TABS_VISIBILITY,
);

export const switchEditorTabAction = createAction<{
    nextTab: EDITOR_TAB_OPTIONS;
}>(APP_ACTIONS.SWITCH_EDITOR_TAB);

// True while the toolbox agent is building a brand-new video (from the
// create_blank_video tool call until the video is persisted and gets an id), so
// the editor can show a loading view immediately instead of waiting for scenes.
export const setToolboxCreatingVideo = createAction<boolean>(
    APP_ACTIONS.SET_TOOLBOX_CREATING_VIDEO,
);

// Tracks which scenes the toolbox agent is currently "reviewing" (e.g. the
// image_ask tool rendering and analyzing a scene), so the build preview can show
// a scanning animation on those scenes.
export const setSceneReviewing = createAction<{
    sceneIds: string[];
    isReviewing: boolean;
}>(APP_ACTIONS.SET_SCENE_REVIEWING);

// True only during the brief View Transition that hands the build preview off to
// the real editor scene list. Both the masonry tiles and the scene-list rows
// apply a shared view-transition-name while this is set so the browser can morph
// each tile into its matching scene; cleared once the transition is done.
export const setSceneHandoffActive = createAction<boolean>(APP_ACTIONS.SET_SCENE_HANDOFF_ACTIVE);

export const setSelectedAspectRatio = createAction<{
    aspectRatio: AspectRatio;
}>(APP_ACTIONS.SET_SELECTED_ASPECT_RATIO);

export const setSelectedTemplateId = createAction<{
    templateId: string | null;
}>(APP_ACTIONS.SET_SELECTED_TEMPLATE_ID);

export const setSelectedPaletteIndex = createAction<{
    paletteIndex: number | null;
}>(APP_ACTIONS.SET_SELECTED_PALETTE_INDEX);

export const setStage = createAction<{
    stage: STAGES;
}>(APP_ACTIONS.SET_STAGE);

export const setVideoId = createAction<{
    videoId: number;
}>(APP_ACTIONS.SET_VIDEO_ID);

export const setVideoCreationSource = createAction<{
    creationSource: CreationSourceEnum | null;
}>(APP_ACTIONS.SET_VIDEO_CREATION_SOURCE);

export const setIsAnimationVideo = createAction<{
    isAnimationVideo: boolean;
}>(APP_ACTIONS.SET_IS_ANIMATION_VIDEO);

export const setParentVideoLanguage = createAction<{
    language: string | null;
}>(APP_ACTIONS.SET_PARENT_VIDEO_LANGUAGE);

export const setVideoDataHash = createAction<{
    videoDataHash: string;
}>(APP_ACTIONS.SET_VIDEO_DATA_HASH);

export const setSharePageUrl = createAction<{
    sharePageUrl: string;
}>(APP_ACTIONS.SET_SHARE_PAGE_URL);

export const setVideoVisibility = createAction<{
    videoVisibility: VideoVisibility;
}>(APP_ACTIONS.SET_VIDEO_VISIBILITY);

export const setVideoLanguage = createAction<{
    video_language: LanguageEnum;
}>(APP_ACTIONS.SET_VIDEO_LANGUAGE);

export const setVideoProject = createAction<{
    projectId: number | null;
}>(APP_ACTIONS.SET_VIDEO_PROJECT);

export const setVideoOwnerId = createAction<{
    videoOwnerId: number;
}>(APP_ACTIONS.SET_VIDEO_OWNER_ID);

export const setVideoApprovalStatus = createAction<{
    status: VideoApprovalStatusEnum | null;
}>(APP_ACTIONS.SET_VIDEO_APPROVAL_STATUS);

export const markSceneGroupAsLoading = createAction<{
    sceneGroupId: string;
    type: SCENE_GROUP_LOADING_TYPES;
    context?: SceneGroupLoadingContextType;
}>(APP_ACTIONS.MARK_SCENE_GROUP_AS_LOADING);

export const markSceneGroupAsFinishedLoading = createAction<{
    sceneGroupId: string;
}>(APP_ACTIONS.MARK_SCENE_GROUP_AS_FINISHED_LOADING);

export const markSceneGroupAsSkipped = createAction<{
    sceneGroupId: string;
}>(APP_ACTIONS.MARK_SCENE_GROUP_AS_SKIPPED);

export const markSceneAsLoading = createAction<{
    sceneId: string;
    type: SCENE_GROUP_LOADING_TYPES;
    context?: SceneGroupLoadingContextType;
}>(APP_ACTIONS.MARK_SCENE_AS_LOADING);

export const markSceneAsFinishedLoading = createAction<{
    sceneId: string;
}>(APP_ACTIONS.MARK_SCENE_AS_FINISHED_LOADING);

export const setIsStoryboardGenerating = createAction<{
    isGenerating: boolean;
}>(APP_ACTIONS.SET_IS_STORYBOARD_GENERATING);

export const setVideoAssignee = createAction<{
    assignee: number | null;
}>(APP_ACTIONS.SET_VIDEO_ASSIGNEE);

export const setVideoFeedbackFormState = createAction<{
    isVisible: boolean;
    source?: string;
}>(APP_ACTIONS.SET_VIDEO_FEEDBACK_FORM_STATE);

export const setRecommendationsCompleted = createAction<{
    completed: boolean;
}>(APP_ACTIONS.SET_RECOMMENDATIONS_COMPLETED);

export const setWindowWidth = createAction<{
    width: number;
}>(APP_ACTIONS.SET_WINDOW_WIDTH);

export const setVideoInitType = createAction<VIDEO_INIT_TYPE>(APP_ACTIONS.SET_VIDEO_INIT_TYPE);

export const setAdvancedMediaControlsState = createAction<{
    isVisible?: boolean;
    sceneId: string;
    assetId: string;
}>(APP_ACTIONS.SET_ADVANCED_MEDIA_CONTROLS_STATE);

export const setChartSettingsState = createAction<{
    isVisible?: boolean;
    sceneId: string;
    assetId: string;
}>(APP_ACTIONS.SET_CHART_SETTINGS_STATE);

export const setTransitionSettingsState = createAction<{
    isVisible?: boolean;
    sceneId: string;
}>(APP_ACTIONS.SET_TRANSITION_SETTINGS_STATE);

export const setBlueprintId = createAction<{
    blueprintId: number | null;
}>(APP_ACTIONS.SET_BLUEPRINT_ID);

export const setAsyncGenerationTaskId = createAction<{
    taskId: string | null;
}>(APP_ACTIONS.SET_ASYNC_GENERATION_TASK_ID);

export const setAsyncGenerationData = createAction<AsyncGenerationData | null>(
    APP_ACTIONS.SET_ASYNC_GENERATION_DATA,
);

export const setRecommendedBlueprint = createAction<{
    recommendedBlueprint: RecommendedBlueprint | null;
}>(APP_ACTIONS.SET_RECOMMENDED_BLUEPRINT);

export const setInitialBlueprintId = createAction<{
    blueprintId: number | null;
}>(APP_ACTIONS.SET_INITIAL_BLUEPRINT_ID);

export const setMediaOptimizerState = createAction<{
    isActive: boolean;
    stage?: 'starting' | 'processing' | 'completing' | 'idle';
    guidance?: string;
}>(APP_ACTIONS.SET_MEDIA_OPTIMIZER_STATE);

export const setDashboardFlow = createAction<{
    dashboardFlow: string | null;
}>(APP_ACTIONS.SET_DASHBOARD_FLOW);

export const setTextOnMediaGenerating = createAction<{
    isGenerating: boolean;
}>(APP_ACTIONS.SET_TEXT_ON_MEDIA_GENERATING);

export const setSkipMediaOptimization = createAction<{
    skip: boolean;
}>(APP_ACTIONS.SET_SKIP_MEDIA_OPTIMIZATION);

export const setVideoCreatedThisSession = createAction<boolean>(
    APP_ACTIONS.SET_VIDEO_CREATED_THIS_SESSION,
);

export const setCreateVideoFlowAnalyticsMethod = createAction<{
    createVideoFlowAnalyticsMethod: NEW_VIDEO_FLOW_CREATION_METHOD | null;
}>(APP_ACTIONS.SET_CREATE_VIDEO_FLOW_ANALYTICS_METHOD);

export const setCreateVideoFlowAnalyticsThemeId = createAction<{
    createVideoFlowAnalyticsThemeId: number | string | null;
}>(APP_ACTIONS.SET_CREATE_VIDEO_FLOW_ANALYTICS_THEME_ID);

export const setLoadingCreatorActivityId = createAction<{
    loadingCreatorActivityId: string | null;
}>(APP_ACTIONS.SET_LOADING_CREATOR_ACTIVITY_ID);

export const setIsEditingDraft = createAction<{
    isEditingDraft: boolean;
}>(APP_ACTIONS.SET_IS_EDITING_DRAFT);

export const setProjectId = createAction<{
    projectId: number | null;
}>(APP_ACTIONS.SET_PROJECT_ID);

export const setAutoCreateSceneGroupFromUploadedAssetUuid = createAction<{
    uuid: string | null;
}>(APP_ACTIONS.SET_AUTO_CREATE_SCENE_GROUP_FROM_UPLOADED_ASSET_UUID);

export const setSelectedBrandKitId = createAction<{
    brandKitId: number | null;
}>(APP_ACTIONS.SET_SELECTED_BRAND_KIT_ID);

export const setPendingChart = createAction<PendingChart | null>(APP_ACTIONS.SET_PENDING_CHART);
